import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router";
import { useAuthContext } from "@/hooks/useAuthContext";
import { useGetAccountByIdQuery } from "@/redux/api/accounts.rtkq";
import {
  useDeleteOfficeMutation,
  useGetOfficeMembersQuery,
  useUpdateOfficeSettingsMutation,
} from "@/redux/api/offices.rtkq";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const fieldClassName =
  "w-full rounded-lg border border-border bg-background px-3 py-2 text-start text-foreground disabled:cursor-not-allowed disabled:opacity-80";
const readonlyFieldClassName =
  "w-full rounded-lg border border-border bg-muted/40 px-3 py-2 text-start text-foreground";
const labelClassName = "mb-1 block text-start text-sm font-medium text-foreground";

const OfficeSettingsPage = () => {
  const { getAccountId, isAccountAdmin, userProfile, refreshUser } =
    useAuthContext();
  const navigate = useNavigate();
  const officeId = getAccountId() || "";
  const canEdit = isAccountAdmin();
  const currentOfficeRole = userProfile?.officeRoles?.find(
    (r) => r.officeId === officeId,
  );
  const isOwner = currentOfficeRole?.isOwner === true;

  const { data: account, isLoading } = useGetAccountByIdQuery(
    { accountId: officeId },
    { skip: !officeId }
  );
  const { data: membersData } = useGetOfficeMembersQuery(officeId, {
    skip: !officeId,
  });
  const [updateSettings, { isLoading: isSaving }] =
    useUpdateOfficeSettingsMutation();
  const [deleteOffice, { isLoading: isDeleting }] = useDeleteOfficeMutation();
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const owner = membersData?.members.find((m) => m.isOwner);
  const ownerLabel = owner
    ? owner.displayName || owner.email || "—"
    : "אין בעלים";
  const otherMembersCount =
    membersData?.members.filter((m) => !m.isOwner).length ?? 0;

  const [name, setName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [city, setCity] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!account) return;
    setName(account.name || "");
    setBusinessName(account.accountInformation?.businessName || "");
    setPhoneNumber(account.accountInformation?.phoneNumber || "");
    setCity(account.accountInformation?.city || "");
  }, [account]);

  if (!officeId) {
    return <Navigate to="/office/dashboard" replace />;
  }

  const handleSave = async () => {
    if (!canEdit) return;
    try {
      await updateSettings({
        officeId,
        name: name || undefined,
        accountInformation: {
          ...(account?.accountInformation ?? {}),
          businessName,
          phoneNumber,
          city,
        },
      }).unwrap();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err: any) {
      alert(err.data?.message || "שגיאה בעדכון ההגדרות");
    }
  };

  const handleDeleteOffice = async () => {
    try {
      await deleteOffice(officeId).unwrap();
      localStorage.removeItem("officeId");
      await refreshUser();
      setIsDeleteConfirmOpen(false);
      toast.success("המשרד נמחק");
      navigate("/", { replace: true });
    } catch (err: any) {
      const code = err?.data?.code || err?.data?.message?.code;
      const message =
        code === "OFFICE_HAS_MEMBERS" ||
        err?.data?.message?.code === "OFFICE_HAS_MEMBERS"
          ? "לא ניתן למחוק משרד כל עוד יש בו חברים נוספים. הסירו אותם קודם."
          : err?.data?.message ||
            err?.data?.message?.message ||
            "שגיאה במחיקת המשרד";
      toast.error(typeof message === "string" ? message : "שגיאה במחיקת המשרד");
    }
  };

  if (isLoading && !account) {
    return (
      <div className="container mx-auto max-w-2xl p-6 text-muted-foreground" dir="rtl">
        טוען...
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-2xl p-6" dir="rtl">
      <h1 className="mb-2 text-start text-2xl font-bold text-foreground">
        הגדרות משרד
      </h1>
      {!canEdit && (
        <p className="mb-6 text-start text-sm text-muted-foreground">
          תצוגה בלבד — רק מנהל או בעלים יכולים לערוך את הגדרות המשרד.
        </p>
      )}
      {canEdit && <div className="mb-6" />}

      <div className="space-y-4 text-start">
        <div>
          <label className={labelClassName}>שם המשרד</label>
          {canEdit ? (
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={fieldClassName}
            />
          ) : (
            <div className={readonlyFieldClassName}>{name || "—"}</div>
          )}
        </div>

        <div>
          <label className={labelClassName}>בעלים</label>
          <div className={readonlyFieldClassName}>
            <div className="font-medium">{ownerLabel}</div>
            {owner?.email && owner.displayName && (
              <div className="truncate text-sm text-muted-foreground">
                {owner.email}
              </div>
            )}
          </div>
        </div>

        <div>
          <label className={labelClassName}>שם העסק</label>
          {canEdit ? (
            <input
              type="text"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              className={fieldClassName}
            />
          ) : (
            <div className={readonlyFieldClassName}>{businessName || "—"}</div>
          )}
        </div>

        <div>
          <label className={labelClassName}>טלפון משרד</label>
          {canEdit ? (
            <input
              type="text"
              inputMode="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className={fieldClassName}
            />
          ) : (
            <div className={readonlyFieldClassName}>{phoneNumber || "—"}</div>
          )}
        </div>

        <div>
          <label className={labelClassName}>עיר</label>
          {canEdit ? (
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className={fieldClassName}
            />
          ) : (
            <div className={readonlyFieldClassName}>{city || "—"}</div>
          )}
        </div>

        <div className="border-t border-border pt-4 text-start">
          <h2 className="mb-2 text-start text-lg font-semibold">מנוי</h2>
          <div className="space-y-1 text-start text-muted-foreground">
            <p>
              סטטוס:{" "}
              <span className="font-medium capitalize text-foreground">
                {account?.subscription?.status || "—"}
              </span>
            </p>
            <p>
              תוכנית:{" "}
              <span className="font-medium text-foreground">
                {account?.subscription?.plan?.name || "—"}
              </span>
            </p>
            <p>
              קרדיטים:{" "}
              <span className="font-medium text-foreground">
                {account?.creditBalance || 0}
              </span>
            </p>
          </div>
        </div>

        {canEdit && (
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="rounded-lg bg-chen-900 px-6 py-2 text-white hover:opacity-90 disabled:opacity-50"
          >
            {isSaving ? "שומר..." : saved ? "נשמר!" : "שמור שינויים"}
          </button>
        )}

        {isOwner && (
          <div className="space-y-3 border-t border-destructive/30 pt-4">
            <h2 className="text-lg font-semibold text-destructive">אזור מסוכן</h2>
            <p className="text-sm text-muted-foreground">
              מחיקת משרד אפשרית רק לבעלים, ורק אם אין חברים נוספים במשרד.
              {otherMembersCount > 0 && (
                <>
                  {" "}
                  כרגע יש {otherMembersCount} חברים נוספים —{" "}
                  <Link to="/office/members" className="text-chen-900 underline">
                    ניהול חברים
                  </Link>
                  .
                </>
              )}
            </p>
            <button
              type="button"
              onClick={() => setIsDeleteConfirmOpen(true)}
              disabled={isDeleting || otherMembersCount > 0}
              className="rounded-lg border border-destructive px-6 py-2 text-destructive hover:bg-destructive/10 disabled:opacity-50"
            >
              מחק משרד
            </button>
          </div>
        )}
      </div>

      <AlertDialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle>מחיקת משרד</AlertDialogTitle>
            <AlertDialogDescription>
              למחוק את המשרד "{name || account?.name || "—"}"? פעולה זו אינה הפיכה
              ממסך זה.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse sm:justify-start">
            <AlertDialogCancel>ביטול</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                void handleDeleteOffice();
              }}
              disabled={isDeleting}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {isDeleting ? "מוחק..." : "מחק משרד"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OfficeSettingsPage;
